import { useRef, useState } from 'react'
import { Download, Upload } from 'lucide-react'
import { useQueryClient } from 'react-query'
import { definitionService } from '../services/api'

interface DefinitionTransferProps {
  scope: 'agents' | 'teams'
}

/**
 * * Downloads or reloads model definitions. Instances already running stay in place.
 */
export function DefinitionTransfer({ scope }: DefinitionTransferProps) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const include = scope === 'agents' ? 'agents' : 'teams,catalog'
  const filename = scope === 'agents' ? 'modeles-agents.json' : 'modeles-equipes.json'

  const download = async () => {
    setBusy(true)
    try {
      const definition = await definitionService.exportDefinitions(include)
      const blob = new Blob([`${JSON.stringify(definition, null, 2)}\n`], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      alert(messageOf(error))
    } finally {
      setBusy(false)
    }
  }

  const onFile = async (file: File | undefined) => {
    if (!file) {
      return
    }
    const confirmed = confirm(
      'Recharger ce fichier ? Les modèles de même nom sont mis à jour. Les modèles absents du fichier et les instances déjà lancées restent en place.'
    )
    if (!confirmed) {
      return
    }
    setBusy(true)
    try {
      const parsed = JSON.parse(await file.text()) as unknown
      const report = await definitionService.importDefinitions(parsed)
      await queryClient.invalidateQueries('agents')
      await queryClient.invalidateQueries('teamTemplates')
      const lines = [
        `Agents créés : ${report.agents.created.length}`,
        `Agents mis à jour : ${report.agents.updated.length}`,
        `Équipes créées : ${report.teams.created.length}`,
        `Équipes mises à jour : ${report.teams.updated.length}`,
      ]
      if (report.catalog.skipped.length > 0) {
        lines.push('Le catalogue n\'a pas été modifié : cette action est réservée à l\'administrateur.')
      }
      alert(lines.join('\n'))
    } catch (error) {
      alert(messageOf(error))
    } finally {
      setBusy(false)
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  return (
    <>
      <button
        type="button"
        className="button button--secondary"
        onClick={() => { void download() }}
        disabled={busy}
      >
        <Download className="w-5 h-5" />
        <span>Exporter</span>
      </button>
      <button
        type="button"
        className="button button--secondary"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
      >
        <Upload className="w-5 h-5" />
        <span>Importer</span>
      </button>
      <input
        id={`definitions-import-${scope}`}
        name={`definitions-import-${scope}`}
        ref={inputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(event) => { void onFile(event.target.files?.[0]) }}
      />
    </>
  )
}

function messageOf(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: { error?: { message?: string } | string } } }).response?.data
    if (data && typeof data.error === 'object' && typeof data.error.message === 'string') {
      return data.error.message
    }
    if (data && typeof data.error === 'string') {
      return data.error
    }
  }
  if (error instanceof SyntaxError) {
    return 'Ce fichier n\'est pas un JSON lisible.'
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'Import ou export impossible.'
}
